import * as React from "react"
import {
  useState,
  useCallback,
  useEffect,
} from "react";
import { graphql } from "gatsby"
import { StaticImage, getImage } from "gatsby-plugin-image"
import Layout from "../components/layout"
import { Seo } from "../components/seo"
import LatestNews from "../components/latestNews";

const TooltipElement = ({ text }) => (
  <button
    type="button"
    className="btn"
    data-bs-toggle="tooltip"
    data-bs-placement="top"
    title={text}
    style={{
      borderRadius: "50%",
      padding: "0",
      marginTop: "-5px",
      marginLeft: "5px",
      textAlign: "center",
      width: "20px",
      height: "20px",
      lineHeight: "20px",
      backgroundColor: "#337ab7",
      color: "white",
      fontWeight: "bold",
    }}
  >
    ?
  </button>
)

const H2 = ({ text }) => (
  <>
    <h2 className="mt-3">{text}</h2>
    <hr />
  </>
)

const IndexPage = ({ data }) => {
  const { news } = data

  const [tollFree, setTollFree] = useState(150);
  const [free, setFree] = useState(0.01);
  const [taxRate, setTaxRate] = useState(20);
  const [count, setCount] = useState(1);
  const [price, setPrice] = useState(199);
  const [shipping, setShipping] = useState(0);
  const [other, setOther] = useState(0);
  const [toll, setToll] = useState(5);
  const [marge, setMarge] = useState(30);

  // Calculated values are all set at once
  const [calculated, setCalculated] = useState({
    sum: 0,
    sumToll: 0,
    sumVat: 0,
    sumAll: 0,
    pricePerPiece: 0,
    pricePerPieceExcl: 0,
    profitPerPiece: 0,
    vatPerPiece: 0,
    profitTotal: 0,
    salesPricePerPiece: 0,
  })

  useEffect(() => {
    if(typeof document !== "undefined") {
      const bootstrap = require("bootstrap")

      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
      tooltipTriggerList.map((item) => {
        return new bootstrap.Tooltip(item)
      })
    }
  }, [])

  // This function re-calculates all values when one of the variables changes
  useEffect(() => {
    const sum = count * price
    const sumToll = (sum <= tollFree) ? 0 : (sum + shipping) * (toll / 100)
    const sumVat = (sum < free) ? 0 : (sum + shipping + sumToll) * (taxRate / 100)
    const sumAll = sum + shipping + other + sumVat + sumToll
    const pricePerPiece = sumAll / count
    const pricePerPieceExcl = (sumAll - sumVat) / count
    const profitPerPiece = pricePerPieceExcl * (marge / 100)
    const vatPerPiece = (pricePerPieceExcl + profitPerPiece) * (taxRate / 100)
    const salesPricePerPiece = pricePerPieceExcl + profitPerPiece + vatPerPiece
    const profitTotal = profitPerPiece * count

    setCalculated({
      sum: sum.toFixed(2),
      sumToll: sumToll.toFixed(2),
      sumVat: sumVat.toFixed(2),
      sumAll: sumAll.toFixed(2),
      pricePerPiece: pricePerPiece.toFixed(2),
      pricePerPieceExcl: pricePerPieceExcl.toFixed(2),
      profitPerPiece: profitPerPiece.toFixed(2),
      vatPerPiece: vatPerPiece.toFixed(2),
      profitTotal: profitTotal.toFixed(2),
      salesPricePerPiece: salesPricePerPiece.toFixed(2),
    })
  }, [
    free,
    tollFree,
    taxRate,
    count,
    price,
    shipping,
    other,
    toll,
    marge,
    setCalculated,
  ])

  const handleTaxRateChange = useCallback((event) => {
    const value = Number(event.target.value)

    setTaxRate(value)
  }, [setTaxRate])

  const handleTollFreeChange = useCallback((event) => {
    const value = Number(event.target.value)

    setTollFree(value)
  }, [setTollFree])

  const handleFreeChange = useCallback((event) => {
    const value = Number(event.target.value)

    setFree(value)
  }, [setFree])

  const handleCountChange = useCallback((event) => {
    const value = Number(event.target.value)

    setCount(value)
  }, [setCount])

  const handlePriceChange = useCallback((event) => {
    const value = Number(event.target.value)

    setPrice(value)
  }, [setPrice])

  const handleShippingChange = useCallback((event) => {
    const value = Number(event.target.value)

    setShipping(value)
  }, [setShipping])

  const handleOtherChange = useCallback((event) => {
    const value = Number(event.target.value)

    setOther(value)
  }, [setOther])

  const handleTollChange = useCallback((event) => {
    const value = Number(event.target.value)

    setToll(value)
  }, [setToll])

  const handleMargeChange = useCallback((event) => {
    const value = Number(event.target.value)

    setMarge(value)
  }, [setMarge])

  return (
    <Layout>

      <div
        className="jumbotron pb-2 mb-3"
        style={{
          backgroundColor: "#eee",
        }}
      >
        <article className="container">
          <h1 className="py-3">
            Importkosten Rechner
          </h1>
          <div className="clearfix mb-3">
            <div className="row">
              <div className="col-12 col-md-8">
                <StaticImage
                  className="mobile-hide float-start me-3 d-none d-md-block"
                  src="../images/port.jpg"
                  title="Importkosten einfach online berechnen"
                  alt="Importkosten einfach online berechnen"
                />
                <div className="">
                  <p>Diese Seite soll dir dabei helfen die Importkosten deiner Bestellung aus dem (nicht EU) Ausland zu berechnen.</p>
                  <p>Ist das Schnäppchen aus China oder den USA wirklich so guenstig wie es scheint? Oft können die Einfuhrumsatzsteuer, der Zoll und Bearbeitungsgebühren noch eine unerwartete extra Belastung werden.</p>
                  <p>Für kommerzielle Importeure gehen wir sogar einen Schritt weiter und bieten auch gleich Berechnung von Gewinn, Marge und Verkaufspreis.</p>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <h4>Neuigkeiten</h4>
                <LatestNews
                  news={news}
                />
              </div>
            </div>
          </div>
        </article>
      </div>

      <section className="container relative bg-light">
        <div className="row g-3 justify-content-end">

          <div className="col-auto d-none d-md-block">
            <label
              htmlFor="country"
              className="mobile-hide col-form-label fw-bold"
            >
              Ich importiere nach
            </label>
          </div>

          <div className="col-6 col-md-2">
            <select
              name="country"
              id="country"
              className="form-control"
              defaultValue={taxRate}
              onChange={handleTaxRateChange}
            >
              <option value="20">Österreich</option>
              <option value="19">Deutschland</option>
              <option value="21">Belgien</option>
              <option value="20">Bulgarien</option>
              <option value="25">Dänemark</option>
              <option value="20">Estland</option>
              <option value="24">Finnland</option>
              <option value="20">Frankreich</option>
              <option value="24">Griechenland</option>
              <option value="23">Irland</option>
              <option value="22">Italien</option>
              <option value="25">Kroatien</option>
              <option value="21">Lettland</option>
              <option value="21">Litauen</option>
              <option value="17">Luxemburg</option>
              <option value="18">Malta</option>
              <option value="21">Niederlande</option>
              <option value="20">Nordirland</option>
              <option value="23">Polen</option>
              <option value="23">Portugal</option>
              <option value="19">Rumänien</option>
              <option value="25">Schweden</option>
              <option value="20">Slowakei</option>
              <option value="22">Slowenien</option>
              <option value="21">Spanien</option>
              <option value="21">Tschechien</option>
              <option value="27">Ungarn</option>
              <option value="19">Zypern</option>
            </select>
          </div>
        </div>

        <H2 text="Freibetr&auml;ge" />

        <div className="row g-3 justify-content-end">
          <div className="col-6 col-md-2">
            <label
              htmlFor="free"
              className="mobile-hide col-form-label fw-bold"
            >
              Abgabenfrei bis

              <TooltipElement
                text="Bis zu diesem Betrag werden weder Zoll noch Eust. verrechnet. Dieser Betrag ist von Land zu Land unterschiedlich."
              />
            </label>
            <input
              name="free"
              id="free"
              className="form-control"
              type="number"
              defaultValue={free}
              onChange={handleFreeChange}
            />
          </div>

          <div className="col-6 col-md-2">
            <label
              htmlFor="toll-free"
              className="mobile-hide col-form-label fw-bold"
            >
              Zollfrei bis

              <TooltipElement
                text="Bis zu diesem Betrag werden keine Zollgeb&uuml;hren verrechnet. Dieser Betrag ist von Land zu Land unterschiedlich."
              />
            </label>
            <input
              name="toll-free"
              id="toll-free"
              className="form-control"
              type="number"
              defaultValue={tollFree}
              onChange={handleTollFreeChange}
            />
          </div>
        </div>

        <H2 text="Einkauf" />

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-3 col-md-auto">
            <label
              htmlFor="price"
              className="mobile-hide col-form-label fw-bold"
            >
              Stückpreis
            </label>
            <input
              name="price"
              id="price"
              className="form-control"
              type="number"
              defaultValue={price}
              onChange={handlePriceChange}
            />
          </div>

          <div className="col-auto align-items-end gx-0">
            <div className="fw-bold col-form-label align-text-bottom">
              x
            </div>
          </div>

          <div className="col-3 col-md-auto">
            <label
              htmlFor="count"
              className="mobile-hide col-form-label fw-bold"
            >
              Anzahl
            </label>
            <input
              name="count"
              id="count"
              className="form-control"
              type="number"
              defaultValue={count}
              onChange={handleCountChange}
            />
          </div>

          <div className="col-auto align-items-end gx-0">
            <div className="fw-bold col-form-label align-text-bottom">
              =
            </div>
          </div>

          <div className="col-4 col-md-2">
            <label
              htmlFor="sum"
              className="mobile-hide col-form-label fw-bold"
            >
              Summe
            </label>
            <input
              name="sum"
              id="sum"
              className="form-control"
              disabled={true}
              value={calculated.sum}
            />
          </div>
        </div>

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-4 col-md-2">
            <label
              htmlFor="shipping"
              className="mobile-hide col-form-label fw-bold"
            >
              Versand
            </label>
            <input
              name="shipping"
              id="shipping"
              className="form-control"
              type="number"
              defaultValue={shipping}
              onChange={handleShippingChange}
            />
          </div>
        </div>

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-4 col-md-2">
            <label
              htmlFor="other"
              className="mobile-hide col-form-label fw-bold"
            >
              Sonstiges

              <TooltipElement
                text="Einige Versanddienstleister erheben eine Pauschale wenn der Zoll über sie abgewickelt wird. Ein Beispiel hierfuer ist die Post."
              />
            </label>
            <input
              name="other"
              id="other"
              className="form-control"
              type="number"
              defaultValue={other}
              onChange={handleOtherChange}
            />
          </div>
        </div>

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              (
            </div>
          </div>

          <div className="col-2 d-none d-md-block">
            <label
              htmlFor="price-1"
              className="mobile-hide col-form-label fw-bold"
            >
              Summe
            </label>
            <input
              name="price-1"
              id="price-1"
              className="form-control"
              disabled={true}
              value={calculated.sum}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              +
            </div>
          </div>

          <div className="col-2 d-none d-md-block">
            <label
              htmlFor="shipping-1"
              className="mobile-hide col-form-label fw-bold"
            >
              Versand
            </label>
            <input
              name="shipping-1"
              id="shipping-1"
              className="form-control"
              disabled={true}
              value={shipping}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              ) x
            </div>
          </div>

          <div className="col-5 col-md-2">
            <label
              htmlFor="toll"
              className="mobile-hide col-form-label fw-bold"
            >
              Zollsatz in %

              <TooltipElement
                text="Der Zollsatz ist von der Warengruppe anhängig und kann via TARIC Datenbank in erfahrung gebracht werden."
              />
            </label>
            <input
              name="toll"
              id="toll"
              className="form-control"
              type="number"
              defaultValue={toll}
              onChange={handleTollChange}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              / 100 =
            </div>
          </div>

          <div className="col-4 col-md-2">
            <label
              htmlFor="sum-toll"
              className="mobile-hide col-form-label fw-bold"
            >
              Zoll
            </label>
            <input
              name="sum-toll"
              id="sum-toll"
              className="form-control"
              disabled={true}
              value={calculated.sumToll}
            />
          </div>
        </div>

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              (
            </div>
          </div>

          <div className="col-2 d-none d-md-block">
            <label
              htmlFor="price-1"
              className="mobile-hide col-form-label fw-bold"
            >
              Summe
            </label>
            <input
              name="price-1"
              id="price-1"
              className="form-control"
              disabled={true}
              value={calculated.sum}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              +
            </div>
          </div>

          <div className="col-2 d-none d-md-block">
            <label
              htmlFor="shipping-1"
              className="mobile-hide col-form-label fw-bold"
            >
              Versand
            </label>
            <input
              name="shipping-1"
              id="shipping-1"
              className="form-control"
              disabled={true}
              value={shipping}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              +
            </div>
          </div>

          <div className="col-2 d-none d-md-block">
            <label
              htmlFor="sum-toll-1"
              className="mobile-hide col-form-label fw-bold"
            >
              Zoll
            </label>
            <input
              name="sum-toll-1"
              id="sum-toll-1"
              className="form-control"
              disabled={true}
              value={calculated.sumToll}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              ) x
            </div>
          </div>

          <div className="col-5 col-md-2">
            <label
              htmlFor="tax-rate-1"
              className="mobile-hide col-form-label fw-bold"
            >
              Eust. in %

              <TooltipElement
                text="Hier wird der Regelsteuersatz für das Land angenommen in welches importiert wird. Für einige Warengruppen gelten verminderte Steuersätze."
              />
            </label>
            <input
              name="tax-rate-1"
              id="tax-rate-1"
              className="form-control"
              type="number"
              value={taxRate}
              onChange={handleTaxRateChange}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              / 100 =
            </div>
          </div>

          <div className="col-4 col-md-2">
            <label
              htmlFor="sum-vat"
              className="mobile-hide col-form-label fw-bold"
            >
              Eust.
            </label>
            <input
              name="sum-vat"
              id="sum-vat"
              className="form-control"
              disabled={true}
              value={calculated.sumVat}
            />
          </div>
        </div>

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-4 col-md-2">
            <label
              htmlFor="price-per-piece-excl"
              className="mobile-hide col-form-label fw-bold"
            >
              Preis / Stück exkl. Eust.
            </label>
            <input
              name="price-per-piece-excl"
              id="price-per-piece-excl"
              className="form-control"
              disabled={true}
              value={calculated.pricePerPieceExcl}
            />
          </div>

          <div className="col-4 col-md-2">
            <label
              htmlFor="price-per-piece"
              className="mobile-hide col-form-label fw-bold"
            >
              Preis / Stück
            </label>
            <input
              name="price-per-piece"
              id="price-per-piece"
              className="form-control"
              disabled={true}
              value={calculated.pricePerPiece}
            />
          </div>

          <div
            className="col-4 col-md-2"
            style={{
              borderTop: "2px solid black",
              marginTop: "32px"
            }}
          >
            <label
              htmlFor="sum-all"
              className="mobile-hide col-form-label fw-bold"
            >
              Gesamt
            </label>
            <input
              name="sum-all"
              id="sum-all"
              className="form-control"
              disabled={true}
              value={calculated.sumAll}
            />
          </div>
        </div>

        <H2 text="Verkauf" />

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-4 col-md-2">
            <label
              htmlFor="price-per-piece-excl-1"
              className="mobile-hide col-form-label fw-bold"
            >
              Preis / Stück
            </label>
            <input
              name="price-per-piece-excl-1"
              id="price-per-piece-excl-1"
              className="form-control"
              disabled={true}
              value={calculated.pricePerPieceExcl}
            />
          </div>
        </div>

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-2 d-none d-md-block">
            <label
              htmlFor="sum-toll-1"
              className="mobile-hide col-form-label fw-bold"
            >
              Preis / Stück
            </label>
            <input
              name="price-per-piece-excl"
              id="price-per-piece-excl"
              className="form-control"
              disabled={true}
              value={calculated.pricePerPieceExcl}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              x
            </div>
          </div>

          <div className="col-5 col-md-2">
            <label
              htmlFor="marge"
              className="mobile-hide col-form-label fw-bold"
            >
              Marge in %
            </label>
            <input
              name="marge"
              id="marge"
              className="form-control"
              value={marge}
              type="number"
              onChange={handleMargeChange}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              / 100 =
            </div>
          </div>

          <div className="col-4 col-md-2">
            <label
              htmlFor="sum-all"
              className="mobile-hide col-form-label fw-bold"
            >
              Gewinn
            </label>
            <input
              name="sum-all"
              id="sum-all"
              className="form-control"
              disabled={true}
              value={calculated.profitPerPiece}
            />
          </div>
        </div>

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              (
            </div>
          </div>

          <div className="col-2 d-none d-md-block">
            <label
              htmlFor="price-2"
              className="mobile-hide col-form-label fw-bold"
            >
              Preis / Stück
            </label>
            <input
              name="price-2"
              id="price-2"
              className="form-control"
              disabled={true}
              value={calculated.pricePerPieceExcl}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              +
            </div>
          </div>

          <div className="col-2 d-none d-md-block">
            <label
              htmlFor="profit-1"
              className="mobile-hide col-form-label fw-bold"
            >
              Gewinn
            </label>
            <input
              name="profit-1"
              id="profit-1"
              className="form-control"
              disabled={true}
              value={calculated.profitPerPiece}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              ) x
            </div>
          </div>

          <div className="col-5 col-md-2">
            <label
              htmlFor="toll"
              className="mobile-hide col-form-label fw-bold"
            >
              Mwst in %

              <TooltipElement
                text="Hier wird der Regelsteuersatz für das Land angenommen in welches importiert wird. Für einige Warengruppen gelten verminderte Steuersätze."
              />
            </label>
            <input
              name="toll"
              id="toll"
              className="form-control"
              type="number"
              defaultValue={taxRate}
              value={taxRate}
              onChange={handleTaxRateChange}
            />
          </div>

          <div className="col-auto align-items-end gx-0 d-none d-md-block">
            <div className="fw-bold col-form-label align-text-bottom">
              / 100 =
            </div>
          </div>

          <div className="col-4 col-md-2">
            <label
              htmlFor="sum-toll"
              className="mobile-hide col-form-label fw-bold"
            >
              Mwst.
            </label>
            <input
              name="sum-toll"
              id="sum-toll"
              className="form-control"
              disabled={true}
              value={calculated.vatPerPiece}
            />
          </div>
        </div>

        <div className="row g-3 justify-content-end align-items-end">
          <div className="col-5 col-md-2">
            <label
              htmlFor="profit-total"
              className="mobile-hide col-form-label fw-bold"
            >
              Gewinn Gesamt
            </label>
            <input
              name="profit-total"
              id="profit-total"
              className="form-control"
              disabled={true}
              value={calculated.profitTotal}
            />
          </div>

          <div
            className="col-4 col-md-2"
            style={{
              borderTop: "2px solid black",
              marginTop: "32px"
            }}
          >
            <label
              htmlFor="sales-price"
              className="mobile-hide col-form-label fw-bold"
            >
              Verkauf
            </label>
            <input
              name="sales-price"
              id="sales-price"
              className="form-control"
              disabled={true}
              value={calculated.salesPricePerPiece}
            />
          </div>
        </div>

        <hr />
        <div className="alert alert-warning" role="alert">
          <strong>Disclaimer:</strong>
          <p>Alle Angaben ohne Gewähr, falls du einen Fehler findest, schreib mir einfach eine Mail.</p>
          <p>Quellen:</p>
          <ul>
            <li>
              <a
                className="link-primary"
                href="https://www.wko.at/service/steuern/Mehrwertsteuersaetze_in_der_EU.html"
                title="Mehrwertsteuersätze in der EU (wko.at)"
                target="_blank"
                rel="noreferrer"
              >
                Mehrwertsteuersätze in der EU (wko.at)
              </a>
            </li>
            <li>
              <a
                className="link-primary"
                href="http://www.awtirol.at/chapter.php?chapterId=318&anth=4"
                title="Einfuhrabgaben (awtirol.at)"
                target="_blank"
                rel="noreferrer"
              >
                Einfuhrabgaben (awtirol.at)
              </a>
            </li>
            <li>
              <a
                className="link-primary"
                href="http://ec.europa.eu/taxation_customs/dds2/taric/taric_consultation.jsp"
                title="TARIC Datenbank (europa.eu)"
                target="_blank"
                rel="noreferrer"
              >
                TARIC Datenbank (europa.eu)
              </a>
            </li>
            <li>
              <a
                className="link-primary"
                href="https://www.bmf.gv.at/zoll/post-internet/internet-shopping.html"
                title="Internet Shopping und Versandhandel (bmf.at)"
                target="_blank"
                rel="noreferrer"
              >
                Internet Shopping und Versandhandel (bmf.at)
              </a>
            </li>
          </ul>
        </div>
      </section>
    </Layout>
  )
}

export default IndexPage

export const Head = ({ data }) => {
  const imageSrc = getImage(data.ogImage)

  return (
    <Seo
      title="Importkosten Rechner für Zoll und Einfuhrumsatzsteuer"
      description="Ist das Schnäppchen aus China oder den USA wirklich so günstig wie es scheint? Überprüfe es mit dem Importkosten Rechner in wenigen Sekunden direkt online."
      image={imageSrc.images.fallback.src}
    />
  )
}

export const pageQuery = graphql`
query {
  news: allMarkdownRemark(
    sort: { frontmatter: { date: DESC } },
    limit: 2
  ) {
    edges {
      node {
        id
        frontmatter {
          date(formatString: "DD.MM.YYYY")
          title
          slug
          description
          image {
            childImageSharp {
              gatsbyImageData(width: 200, height: 200)
            }
          }
        }
      }
    }
  }
  ogImage: file(relativePath: { eq: "port.jpg" }) {
    childImageSharp {
      gatsbyImageData(width: 1200, height: 630)
    }
  }
}
`